
import cookies from 'js-cookie';
import { defineComponent, ref, Ref, SetupContext, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import device from '@/common/device-info';
import VideoSwiper from '@/components/video-swiper/index.vue';
import { autoLaunchAppWarp } from '@/common/call-app';
import {
    genWebDidAndSessionId,
    getQuery,
    isPwa,
    genCheckPwaParams,
    getTargetUrlFromWebOrDesktop,
    getNetworkType,
    commonPvParams,
} from '@/common/utils';
import { header, checkSnack } from '@/common/seo';
import { setCommonParamsCookiesInServer } from '@/common/common-params';
import { immediateSendLog, getKwaiLog } from '@/common/radar';

export default defineComponent({
    components: {
        VideoSwiper,
    },
    setup(props, ctx: SetupContext) {
        const fmp_time = Date.now();
        const videoList: Ref<Array<Record<string, any>>> = ref([]);
        const guideShow = ref(false); // 教程
        const isIos = ref(false);
        const beginTime = ref(0);

        const { proxy }: any = getCurrentInstance();
        const { photoId = '', share_device_id, shareBucket } = proxy.$route.query;

        const getVideoList = async (photoId?: string) => {
            const res = await proxy.$axios.$$post('overseaAPI::/rest/o/w/share/pwa/feed', {
                photoId,
                shareDeviceId: share_device_id,
                shareBucket,
                fromPage: 'PWA_FOR_YOU',
            });

            const { result, feeds, index, llsid } = res || {};

            return {
                result,
                index,
                llsid,
                feeds,
            };
        };

        // 获取更多
        const handleLoadMore = async () => {
            const { feeds } = await getVideoList();
            const videoPlayQueue: any = videoList.value;
            videoPlayQueue.push(...feeds);
            videoList.value = videoPlayQueue;
        };

        const handleRefresh = async () => {
            const { feeds } = await getVideoList();
            videoList.value = feeds;
        };

        const consume_duration = () => {
            const consume_duration = new Date().getTime() - beginTime.value;
            const webLogger = getKwaiLog();
            immediateSendLog({
                type: 'click',
                name: 'PAGE_CONSUME_DURATION',
                value: {
                    consume_duration,
                },
            });
            // 停留时长
            webLogger.sendImmediately('PV', {
                type: 'leave',
                beginTime,
            });
            // 每次触发离开事件 都重新计时
            beginTime.value = new Date().getTime();
        };

        const handleVisibilityChange = () => {
            const consume_duration = new Date().getTime() - beginTime.value;
            // 切换回来重新计时
            if (document.visibilityState === 'visible') {
                beginTime.value = new Date().getTime();
            } else {
                immediateSendLog({
                    type: 'click',
                    name: 'PAGE_CONSUME_DURATION',
                    value: {
                        consume_duration,
                    },
                });
                beginTime.value = new Date().getTime();
            }
        };

        const sendPv = () => {
            const params = commonPvParams({ defaultPageSource: 'foryou' });
            // 获取启动config
            const country = proxy.$store.state.startup.countryInfo;
            const user_id = cookies.get('user_id');
            const referrer = document?.referrer;

            immediateSendLog({
                type: 'pv',
                name: 'SHARE_PWA_PAGE_H5',
                value: {
                    country,
                    user_id,
                    ...params,
                },
            });
        };

        onMounted(async () => {
            // 记录页面打开时间
            beginTime.value = new Date().getTime();
            const webLogger = getKwaiLog();
            isIos.value = device().isIOS;

            try {
                webLogger?.plugins?.radar?.fmp(fmp_time);
            } catch (error) {
                console.error(error);
            }
            const { pwa_source } = getQuery();
            proxy.$bus.on('sendPv', sendPv);
            // 初始化发送pv埋点，登录成功后获取到user_id 继续上报一次
            sendPv();
            // 是不是需要记录一下拉起来多少
            setTimeout(() => {
                autoLaunchAppWarp(`ikwai://home?target_url=${getTargetUrlFromWebOrDesktop()}`);
            }, 500);

            // a标签拉app会触发beforeunload 所以在拉完之后注册
            window.addEventListener('beforeunload', consume_duration);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            // 从icon进入 请求新的视频资源
            if (pwa_source === 'desktop') {
                const res = await getVideoList();
                if (res.result === 1) {
                    videoList.value = [...res.feeds];
                }
            } else {
                // 小于3个 也再请求一次
                // eslint-disable-next-line no-lonely-if
                if (proxy.photoList.length < 3) {
                    const res = await getVideoList(photoId as string);
                    if (res.result === 1) {
                        videoList.value = [...proxy.photoList, ...res.feeds];
                    }
                } else {
                    // 第一次进入 从node提取
                    videoList.value = proxy.photoList;
                }
            }
        });

        onUnmounted(() => {
            proxy.$bus.emit('isLoad', false);
            window.removeEventListener('beforeunload', consume_duration);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        });

        return {
            videoList,
            isIos,
            handleLoadMore,
            guideShow,
            handleRefresh,
        };
    },
    async asyncData(ctx) {
        let photoList = [];

        if (process.server) {
            const { photoId, share_device_id, shareBucket } = ctx.route.query;
            setCommonParamsCookiesInServer({
                bucket: ctx.store.state.startup.bucket,
                host: ctx.req.headers.host,
                ctx,
                countryInfo: ctx.store.state.startup.countryInfo,
            });

            try {
                const res = await ctx.$axios.$$post('overseaAPI::/rest/o/w/share/pwa/feed', {
                    photoId,
                    shareDeviceId: share_device_id,
                    shareBucket,
                    fromPage: 'PWA_FOR_YOU',
                });

                if (res.result === 1) {
                    photoList = res.feeds;
                }
            } catch (error) {
                console.log(error);
            }
        }
        return {
            photoList,
            metaInfoData: [],
        };
    },
    head() {
        const head = header(this);

        const isSnack = this.$isSnack;

        const meta = head?.meta;

        const existingMeta: { [key: string]: boolean } = {
            'og:title': false,
            'og:description': false,
            keywords: false,
            description: false,
            'og:site_name': false,
        };

        meta?.forEach((item: any) => {
            if (item?.property in existingMeta) {
                existingMeta[item.property] = true;
            } else if (item?.name in existingMeta) {
                existingMeta[item.name] = true;
            }
        });

        if (!existingMeta['og:title']) {
            meta?.push({
                property: 'og:title',
                hid: 'og:title',
                content: isSnack ? 'Snack' : 'Kwai',
            });
        }

        if (!existingMeta['og:description']) {
            meta?.push({
                property: 'og:description',
                hid: 'og:description',
                content: isSnack ? 'Snack' : 'Kwai',
            });
        }

        if (!existingMeta.keywords) {
            meta?.push({
                name: 'keywords',
                content: isSnack ? 'Snack' : 'Kwai',
            });
        }

        if (!existingMeta.description) {
            meta?.push({
                name: 'description',
                hid: 'description',
                content: isSnack ? 'Snack' : 'Kwai',
            });
        }

        if (!existingMeta['og:site_name']) {
            meta?.push({
                hid: 'og:site_name',
                property: 'og:site_name',
                content: isSnack ? 'Snack' : 'Kwai',
            });
        }

        return {
            ...head,
        };
    },
});
